import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProjectsManager from "@/components/ProjectsManager";
import CampaignPerformance from "@/components/CampaignPerformance";
import SelfFundedPanel from "@/components/SelfFundedPanel";
import { toast } from "sonner";
import { LogOut, LayoutGrid, Mail, FileText, Send, Globe2, KeyRound, Download, CreditCard, TrendingUp } from "lucide-react";

async function downloadCsv(dataset, label) {
    try {
        const res = await api.get(`/admin/export/${dataset}.csv`, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `zsgi-${dataset}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success(`${label} exported`);
    } catch {
        toast.error("Export failed");
    }
}

function ExportButton({ dataset, label }) {
    return (
        <button onClick={() => downloadCsv(dataset, label)} className="btn-ghost" data-testid={`export-${dataset}`}>
            <Download className="w-4 h-4" /> Export CSV
        </button>
    );
}

const TABS = [
    { key: "overview", label: "Overview", icon: LayoutGrid },
    { key: "campaign", label: "Campaign Performance", icon: TrendingUp },
    { key: "self-funded", label: "Self-Funded", icon: CreditCard },
    { key: "contacts", label: "Enquiries", icon: Mail },
    { key: "applications", label: "Applications", icon: FileText },
    { key: "movement", label: "Movement", icon: Globe2 },
    { key: "brochures", label: "Brochures", icon: Download },
    { key: "payments", label: "Payments", icon: CreditCard },
    { key: "newsletter", label: "Newsletter", icon: Send },
    { key: "projects", label: "Map Projects", icon: Globe2 },
    { key: "password", label: "Password", icon: KeyRound },
];

function fmtDate(iso) {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function DataTable({ columns, rows, testid }) {
    if (!rows.length) return <div className="label-mono text-[var(--ivory)]/40 py-16 text-center" data-testid={`${testid}-empty`}>NO RECORDS YET</div>;
    return (
        <div className="overflow-x-auto border-t border-l border-[rgba(255,255,240,0.12)]" data-testid={testid}>
            <div className="min-w-[720px]">
                <div className="grid text-left" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0,1fr))` }}>
                    {columns.map((c) => (
                        <div key={c.key} className="label-mono text-[var(--gold)] p-3 border-r border-b border-[rgba(255,255,240,0.12)] bg-[var(--navy-deep)]">{c.label}</div>
                    ))}
                    {rows.map((r, i) => columns.map((c) => (
                        <div key={c.key + i} className="p-3 border-r border-b border-[rgba(255,255,240,0.12)] text-sm text-[var(--ivory)]/80 break-words">
                            {c.render ? c.render(r) : (r[c.key] ?? "—")}
                        </div>
                    )))}
                </div>
            </div>
        </div>
    );
}

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("overview");
    const [stats, setStats] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [applications, setApplications] = useState([]);
    const [movement, setMovement] = useState([]);
    const [brochures, setBrochures] = useState([]);
    const [payments, setPayments] = useState([]);
    const [newsletter, setNewsletter] = useState([]);
    const [projects, setProjects] = useState([]);
    const [pw, setPw] = useState({ current_password: "", new_password: "" });

    const loadAll = useCallback(async () => {
        try {
            const [s, c, a, m, b, pay, n, p] = await Promise.all([
                api.get("/admin/stats"), api.get("/admin/contacts"), api.get("/admin/applications"),
                api.get("/admin/movement-signups"), api.get("/admin/brochure-leads"), api.get("/admin/payments"),
                api.get("/admin/newsletter"), api.get("/projects"),
            ]);
            setStats(s.data); setContacts(c.data.items); setApplications(a.data.items);
            setMovement(m.data.items); setBrochures(b.data.items); setPayments(pay.data.items);
            setNewsletter(n.data.items); setProjects(p.data.projects);
        } catch (e) {
            toast.error("Session expired. Please sign in again.");
        }
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    const reloadProjects = () => api.get("/projects").then(({ data }) => setProjects(data.projects));

    const doLogout = () => { logout(); navigate("/admin/login"); };

    const changePassword = async (e) => {
        e.preventDefault();
        try {
            await api.post("/auth/change-password", pw);
            toast.success("Password changed");
            setPw({ current_password: "", new_password: "" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const statCards = stats ? [
        { label: "Enquiries", value: stats.contacts, tab: "contacts" },
        { label: "Applications", value: stats.applications, tab: "applications" },
        { label: "Movement signups", value: stats.movement, tab: "movement" },
        { label: "Brochure leads", value: stats.brochures, tab: "brochures" },
        { label: "Payments", value: stats.payments, tab: "payments" },
        { label: "Self-Funded Regs", value: stats.self_funded, tab: "self-funded" },
        { label: "Newsletter", value: stats.newsletter, tab: "newsletter" },
        { label: "Map projects", value: stats.projects, tab: "projects" },
    ] : [];

    return (
        <div className="min-h-screen bg-[var(--navy)]" data-testid="admin-dashboard">
            <div className="grain-overlay" aria-hidden />
            {/* Header */}
            <header className="hairline-b sticky top-0 z-30 bg-[rgba(6,17,36,0.9)] backdrop-blur-lg">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="serif-display text-2xl">Z<span className="text-[var(--gold)]">•</span>SGI</span>
                        <span className="label-mono text-[var(--ivory)]/50">ADMIN CONSOLE</span>
                    </div>
                    <div className="flex items-center gap-5">
                        <span className="label-mono text-[var(--ivory)]/60 hidden sm:block">{user?.email}</span>
                        <button onClick={doLogout} className="label-mono flex items-center gap-2 text-[var(--ivory)]/80 hover:text-[var(--gold)]" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Logout</button>
                    </div>
                </div>
            </header>

            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-10 grid md:grid-cols-12 gap-8">
                {/* Sidebar */}
                <nav className="md:col-span-3 lg:col-span-2">
                    <ul className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible">
                        {TABS.map((t) => {
                            const Icon = t.icon;
                            return (
                                <li key={t.key}>
                                    <button
                                        onClick={() => setTab(t.key)}
                                        className={`label-mono flex items-center gap-3 px-4 py-3 w-full text-left whitespace-nowrap transition-colors border-l-2 ${
                                            tab === t.key ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(201,162,75,0.08)]" : "border-transparent text-[var(--ivory)]/60 hover:text-[var(--ivory)]"
                                        }`}
                                        data-testid={`admin-tab-${t.key}`}
                                    >
                                        <Icon className="w-4 h-4" /> {t.label}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </nav>

                {/* Content */}
                <main className="md:col-span-9 lg:col-span-10">
                    {tab === "overview" && (
                        <div>
                            <h1 className="serif-display text-4xl md:text-5xl mb-8">Welcome back<span className="text-[var(--gold)]">.</span></h1>
                            <div className="grid grid-cols-2 lg:grid-cols-5 border-t border-l border-[rgba(255,255,240,0.12)]">
                                {statCards.map((s) => (
                                    <button key={s.label} onClick={() => setTab(s.tab)} className="p-6 border-r border-b border-[rgba(255,255,240,0.12)] text-left hover:bg-[rgba(201,162,75,0.06)] transition-colors" data-testid={`stat-${s.tab}`}>
                                        <div className="serif-display text-4xl md:text-5xl text-[var(--gold)]">{s.value}</div>
                                        <div className="label-mono mt-2 text-[var(--ivory)]/60">{s.label}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {tab === "campaign" && (
                        <CampaignPerformance />
                    )}

                    {tab === "self-funded" && (
                        <SelfFundedPanel />
                    )}

                    {tab === "contacts" && (
                        <><div className="flex items-center justify-between mb-6"><h1 className="serif-display text-3xl md:text-4xl">Enquiries</h1><ExportButton dataset="contacts" label="Enquiries" /></div>
                        <DataTable testid="contacts-table" rows={contacts} columns={[
                            { key: "name", label: "Name" }, { key: "email", label: "Email" }, { key: "phone", label: "Phone" },
                            { key: "organization", label: "Org" }, { key: "message", label: "Message" },
                            { key: "created_at", label: "When", render: (r) => fmtDate(r.created_at) },
                        ]} /></>
                    )}

                    {tab === "applications" && (
                        <><div className="flex items-center justify-between mb-6"><h1 className="serif-display text-3xl md:text-4xl">Applications</h1><ExportButton dataset="applications" label="Applications" /></div>
                        <DataTable testid="applications-table" rows={applications} columns={[
                            { key: "full_name", label: "Name" }, { key: "email", label: "Email" }, { key: "phone", label: "Phone" },
                            { key: "program_slug", label: "Program" }, { key: "country", label: "Country" }, { key: "role", label: "Role" },
                            { key: "created_at", label: "When", render: (r) => fmtDate(r.created_at) },
                        ]} /></>
                    )}

                    {tab === "movement" && (
                        <><div className="flex items-center justify-between mb-6"><h1 className="serif-display text-3xl md:text-4xl">Movement signups</h1><ExportButton dataset="movement-signups" label="Movement signups" /></div>
                        <DataTable testid="movement-table" rows={movement} columns={[
                            { key: "name", label: "Name" }, { key: "email", label: "Email" }, { key: "region", label: "Region" },
                            { key: "interest", label: "Interest" }, { key: "note", label: "Note" },
                            { key: "created_at", label: "When", render: (r) => fmtDate(r.created_at) },
                        ]} /></>
                    )}

                    {tab === "brochures" && (
                        <><div className="flex items-center justify-between mb-6"><h1 className="serif-display text-3xl md:text-4xl">Brochure leads</h1><ExportButton dataset="brochure-leads" label="Brochure leads" /></div>
                        <DataTable testid="brochures-table" rows={brochures} columns={[
                            { key: "name", label: "Name" }, { key: "email", label: "Email" },
                            { key: "program_name", label: "Brochure" }, { key: "program_slug", label: "Program" },
                            { key: "created_at", label: "When", render: (r) => fmtDate(r.created_at) },
                        ]} /></>
                    )}

                    {tab === "payments" && (
                        <><div className="flex items-center justify-between mb-6"><h1 className="serif-display text-3xl md:text-4xl">Payments</h1><ExportButton dataset="payments" label="Payments" /></div>
                        <DataTable testid="payments-table" rows={payments} columns={[
                            { key: "name", label: "Name" }, { key: "email", label: "Email" },
                            { key: "label", label: "For" },
                            { key: "amount", label: "Amount", render: (r) => `${(r.amount / 100).toFixed(2)} ${r.currency}` },
                            { key: "status", label: "Status", render: (r) => <span className={r.status === "paid" ? "text-[var(--emerald)]" : "text-[var(--ivory)]/50"}>{r.status}</span> },
                            { key: "payment_id", label: "Ref" },
                            { key: "created_at", label: "When", render: (r) => fmtDate(r.created_at) },
                        ]} /></>
                    )}

                    {tab === "newsletter" && (
                        <><div className="flex items-center justify-between mb-6"><h1 className="serif-display text-3xl md:text-4xl">Newsletter subscribers</h1><ExportButton dataset="newsletter" label="Newsletter" /></div>
                        <DataTable testid="newsletter-table" rows={newsletter} columns={[
                            { key: "email", label: "Email" }, { key: "name", label: "Name" },
                            { key: "created_at", label: "Subscribed", render: (r) => fmtDate(r.created_at) },
                        ]} /></>
                    )}

                    {tab === "projects" && (
                        <><h1 className="serif-display text-3xl md:text-4xl mb-6">Map projects</h1>
                        <ProjectsManager projects={projects} reload={reloadProjects} /></>
                    )}

                    {tab === "password" && (
                        <div className="max-w-md">
                            <h1 className="serif-display text-3xl md:text-4xl mb-6">Change password</h1>
                            <form onSubmit={changePassword} className="space-y-6" data-testid="change-password-form">
                                <div><label className="label-mono block mb-2">Current password</label>
                                    <input required type="password" className="input-editorial" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="cp-current" /></div>
                                <div><label className="label-mono block mb-2">New password (min 8 chars)</label>
                                    <input required type="password" className="input-editorial" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="cp-new" /></div>
                                <button className="btn-gold" data-testid="cp-submit">Update password</button>
                            </form>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
